/**
 * One sidebar row: a fixed channel, an agent, or a specialist.
 *
 * A specialist row is NOT its own channel — clicking it opens its OWNER's
 * channel (spec #44 §1, §Provisional 8, and the same rule #46 encodes in
 * the bridge edge). That is why `domain` on a specialist row is the
 * owner's domain, not the specialist's key.
 */

import { PresenceDot, PRESENCE_LABEL } from './PresenceDot';
import { UnreadBadge } from './UnreadBadge';
import type { PresenceState } from '../../types/workspace';
import styles from './workspace.module.css';

export interface ChannelRowProps {
  testId: string;
  label: string;
  onOpen: () => void;
  open?: boolean;
  presence?: PresenceState;
  unread?: boolean;
  unreadCount?: number;
  /** Specialist rows are indented under their owner's roster. */
  indented?: boolean;
  /** The `#`/`▦` sigil for the three fixed channels. */
  sigil?: string;
  unreadKey?: string;
}

export function ChannelRow({
  testId,
  label,
  onOpen,
  open = false,
  presence,
  unread = false,
  unreadCount,
  indented = false,
  sigil,
  unreadKey,
}: ChannelRowProps) {
  const muted = presence === 'off' || presence === 'not-set-up';
  const classes = [
    styles.row,
    open ? styles.rowOpen : '',
    muted ? styles.rowMuted : '',
    indented ? styles.rowSpecialist : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
      data-presence={presence ?? undefined}
      onClick={onOpen}
    >
      {presence ? <PresenceDot state={presence} /> : null}
      {sigil ? <span aria-hidden="true">{sigil}</span> : null}
      <span className={styles.rowLabel}>{label}</span>
      {presence === 'not-set-up' ? (
        <span className={styles.subtle}>{PRESENCE_LABEL['not-set-up']}</span>
      ) : null}
      {unreadCount !== undefined ? (
        <UnreadBadge domain={unreadKey ?? testId} count={unreadCount} />
      ) : null}
      {unread && unreadCount === undefined ? (
        <UnreadBadge domain={unreadKey ?? testId} />
      ) : null}
    </button>
  );
}
